/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */
define(['../appContext',
    '../utils/expressionEngine',
    '../utils/tools',
    '../support/esprima',
    '../support/escodegen.browser',
    '../utils/dvtypes'],
function (app, exprEng, tools, esprima, escodegen, types) {
    const PropertyDataType = types.PropertyDataType;
    let expression = {};

    expression.keywords = new Map([
        ["ascending", types.sorting.asc],
        ["descending", types.sorting.desc],
        ["none", types.sorting.none],
        ["invisible", 0],
        ["visible", 1],
    ]);

    /**
     * This will create an expression that may be evaluated to return a value.
     *
     * @/// <param name="propertyToCalc" type="Object">Property object that will be checked for its definition; if the property is set to calculate only once and it is not a new value, IT WILL NOT EXECUTE THE CALCULATION and instead just return the current value.</param>
     * @/// <param name="drivingViewID" type="Integer">The ID of the context view.</param>
     * @/// <param name="formula" type="String">The formula to evaluate.</param>
     * @/// <param name="status" type="String">The status to set the property to when evaluateStatus() is called.</param>
     */
    expression.create = function (propertyToCalc, drivingViewID, formula, status, mvc) {
        var ex = {};

        // handle raw formula expression consumers.
        if (arguments.length == 1) {
            formula = propertyToCalc;
            propertyToCalc = null;
            drivingViewID = 0;
            status = null;
            mvc = null;
        }

        // #region Properties

        // The property that this expression is defined for...AL
        ex.propertyToCalc = propertyToCalc ? propertyToCalc : null;

        // A reference to the calling context viewID...AL
        ex.drivingViewID = drivingViewID;

        // For troubleshooting purposes, see the formula as stored on the definition (to compare against the parsed formula) ... EK
        ex.rawFormula = formula;

        // The formula that defines this expression...AL
        ex.formula = formula;

        // A list of property IDs that are defined in the string...AL
        ex.propertyIDs = [];

        // Collection of all the dependency records that this expression will need (keyed on viewID)...AL
        ex.gridRecords = new tools.dictionary();

        // When specified, this status is set to a value where a true expression evaluation has occurred...AL
        ex.status = status ? status : null;

        //the mvc context...TD
        ex.mvc = mvc;

        // #endregion

        // #region Parsing

        if (ex.formula != null && formula.indexOf("//runAs JavaScript") < 0) {
            // Parse the expression string on creation for the static stuff (i.e. getting a list of property IDs as well as replacing "[_propID_]" with "vfp(_viewID_, _propID_)"...AL
            (function parseOnCreate() {

                // First trim the string...AL
                ex.formula = String(ex.formula).trim();

                ex.formula = singleToDoubleQuotes(ex.formula);

                // Split the entire string on single quotes for processing the logical strings (i.e. the actual javascript logic that will be evaled) vs the actual strings ('string')...AL
                // If the first position is a single quote, then the expression starts with (or is entirely) a string..AL
                var isString = ex.formula[0] == '"',
                    splitArr = ex.formula.split('"'),
                    currStr;

                if (isString == true && splitArr.length > 1) { splitArr.splice(0, 1); }

                for (var i = 0, il = splitArr.length; i < il; i++) {
                    currStr = splitArr[i];

                    if (isString == true) {
                        // This is an actual string segment, there is nothing to parse here (note that the escaped single quotes will be added back in at the end of processing)...AL
                        isString = false;
                        if (i == 0) { splitArr[i] = '"' + splitArr[i]; }
                        else if (i == splitArr.length - 1) { splitArr[i] = splitArr[i] + '"'; }

                    } else {
                        isString = true;
                        // This segment is actual code that will be evaled, parse the string apprpriately...AL
                        splitArr[i] = parseLogicString(currStr.toLowerCase()); // Normalize all logical strings to lower case (dev express function calls are not case sensitive)...AL
                    }
                }

                ex.formula = splitArr.join('"');

                //Parse the DV values that are used by this expression (if any)...EK
                parseValues();

                //Parse the DV values into GV objects that are used by this expression (if any)...BC
                parseForGridValues();

                //Execute the variable parse that replaces variables with a function to get the variable's current value...EK
                parseVars();

                //Clean the final expression to account for non-standard JS operators...EK
                cleanNonStandardOps();

                // This is the core function to parsing the logical javascript strings in the expression...AL
                function parseLogicString(str) {

                    // Carraige returns are only here b/c of the expression designer in dev express, they need to be removed or they will bomb the evaulation...AL
                    str = str.replace(new RegExp("\r", "g"), " ");

                    // Dev express uses a single = for evaluation.  This must be ==, otherwise it will set the value to what it's comparing against...AL
                    str = str.replace(new RegExp("=", "gi"), "==");

                    //NOTE: TODO: This is ugly.. we need to limit the spacing using the HTML5 designer when it is done...EK
                    str = str.replace(new RegExp("====", "gi"), "==");
                    str = str.replace(new RegExp(">==", "gi"), ">=");
                    str = str.replace(new RegExp("<==", "gi"), "<=");
                    str = str.replace(new RegExp("!==", "gi"), "!=");
                    str = str.replace(new RegExp("\\?", "g"), "null");

                    // Replace the keywords with their mapped value
                    expression.keywords.forEach((value, keyword) => {
                        str = str.replace(new RegExp(`(^|[^\\w.])${keyword}((?![\\w.]|(\\s*\\())|$)`, 'gi'), `$1${value}$2`);
                    });

                    return str;
                }
            })();
        }
        else {
            //else : We are running live javascript, don't parse anything...EK
        }

        /**
         * Attempts to convert any single quote string indicators to double quotes
         * @param {string} formula - the formula to update
         * @returns {string} The formula with all string identifiers as double quotes
         */
        function singleToDoubleQuotes(formula) {
            //Update all string identifiers to double quotes...EK
            let tempString = [],
                activeStringQuote = null;

            for (let currChar of formula) {
                if (currChar === '"' || currChar === "'" || currChar === "`") {
                    if (activeStringQuote != null && activeStringQuote !== currChar) {
                        // Inside a different type of string
                        tempString.push('" + char(' + currChar.charCodeAt(0) + ') + "');
                    }
                    else {
                        // Beginning or end of the current quote string
                        tempString.push('"');
                        activeStringQuote = (activeStringQuote === currChar) ? null : currChar;
                    }
                }
                else {
                    const currCharCode = currChar.charCodeAt(0);
                    if (activeStringQuote != null && (currCharCode === 10 || currCharCode === 13 || currCharCode === 92)) {
                        // escape new lines (10), returns (13), and backslashes (92)
                        tempString.push('" + char(' + currCharCode + ') + "');
                    }
                    else {
                        tempString.push(currChar);
                    }
                }
            }

            return tempString.join('');
        }

        function cleanNonStandardOps() {
            var syntaxObj = esprima.parse(ex.formula);
            visitor(syntaxObj, function (el) {
                if (el.type === "BinaryExpression") {
                    if (el.operator === "like") {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_like", type: "Identifier" };
                        el.arguments = [el.left, el.right];
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                    else if (el.operator === 'not like') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_notlike", type: "Identifier" };
                        el.arguments = [el.left, el.right];
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                    else if (el.operator === 'in') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_in", type: "Identifier" };
                        if (el.right.expressions != null) {
                            el.arguments = [el.left].concat(el.right.expressions);
                        }
                        else {
                            el.arguments = [el.left].concat(el.right);
                        }

                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                    else if (el.operator === 'not in') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_notin", type: "Identifier" };
                        if (el.right.expressions != null) {
                            el.arguments = [el.left].concat(el.right.expressions);
                        }
                        else {
                            el.arguments = [el.left].concat(el.right);
                        }
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                    else if (el.operator === 'or') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_or", type: "Identifier" };
                        el.arguments = [el.left, el.right];
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                    else if (el.operator === 'and') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_and", type: "Identifier" };
                        el.arguments = [el.left, el.right];
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                    else if (el.operator === '<>') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_notequal", type: "Identifier" };
                        el.arguments = [el.left, el.right];
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                }
                else if (el.type === 'UnaryExpression') {
                    if (el.operator === 'not') {
                        el.type = "CallExpression";
                        el.callee = { name: "operator_not", type: "Identifier" };
                        el.arguments = [el.argument];
                        delete el.operator;
                        delete el.left;
                        delete el.right;
                    }
                }
                else if (el.type === 'CallExpression' && el.callee != null && el.callee.type === 'Identifier') {
                    // We found a function call in the expression.  We need to prepend the "expreng." to the function so the eval has scope to execute it...EK
                    //Temp fix for double expression construction...KB
                    if (el.callee.name.startsWith('operator') === false) {
                        let name = el.callee.name;
                        if (el.callee.name.startsWith('expreng.expr.') === false) {
                            el.callee.name = 'expreng.expr.' + el.callee.name;
                        }
                        else {
                            name = el.callee.name.replace('expreng.expr.','');
                        }
                        if (name) {
                            const expr = exprEng.expr[name];
                            if (expr != null && expr.updatesOnData) {
                                ex.usesDataDrivenFunctions = true;
                            }
                        }
                    }
                }
            });

            function visitor(tree, visit) {
                for (var i in tree) {
                    if (tree[i] != null) {
                        visit(tree[i]);
                    }
                    if (typeof tree[i] === "object" && tree[i] !== null) {
                        visitor(tree[i], visit);
                    }
                }
            }

            //Actually take the object that represents the logic and turn it into executable JS...EK
            ex.formula = escodegen.generate(syntaxObj);
        }

        function parseValues() {
            // Replace all instances of "['Property GUID']" with a call to the ex.vfp function (and pass in the viewID and property ID to the function)...AL
            var matchSet = ex.formula.match(/(\[{1}\"{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\"{0,1}\]{1})/g),
                currMatch,
                currPropID;
            for (var i in matchSet) {
                currMatch = matchSet[i];

                currPropID = currMatch.replace(/[\[\]\"]/g, '');
                if (ex.propertyIDs.filter(o => o.UID === currPropID).length === 0) {
                    ex.propertyIDs.push({ "Type": "Property", "UID" : currPropID });
                }
                // Note - right now, we will only ever use the caller's viewID (until we support cross mvc calls in the expression designer)...AL
                ex.formula = ex.formula.replace(new RegExp(currMatch.escapeChars(), "g"), 'expreng.currexpression.vfp(' + ex.drivingViewID + ', \"' + currPropID + '\")');
            }

            //See if the newUID option was used, if so, replace with the function to return a new UID...EK
            ex.formula = ex.formula.replace(/\[newuid\]/g, 'app.dv.tools.newUID()');
        }

        function parseForGridValues() {
            // Replace all instances of "<'Property GUID'>" with their respective grid Value
            var matchSet = ex.formula.match(/(\<{1}\'{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\'{0,1}\>{1})/g),
                currMatch,
                currPropID;

            for (var i in matchSet) {
                currMatch = matchSet[i];

                currPropID = currMatch.replace(/[\<\>\"]/g, '');
                if (ex.propertyIDs.where(function (o) { return (o.UID == currPropID); }).length == 0) {
                    ex.propertyIDs.push({ "Type": "Property", "UID" : currPropID });
                }
                ex.formula = ex.formula.replace(new RegExp(currMatch.escapeChars(), "g"), 'expreng.currexpression.vfpgv(' + ex.drivingViewID + ', \"' + currPropID + '\")');
            }
        }

        // Swap out tokens for session variables where appropriate....AL
        function parseVars() {
            // if no expression, nothing to parse...WR
            if (ex.formula == null) { return; }
            var matchSet = ex.formula.match(/[^{\}]+(?=})/g);

            //If we have no matches, just jump out...EK
            if (matchSet == null) { return; }

            //Because of the regexp matching, and the fact that we are replacing the same value with the value used to search, this matchset MUST be unique...EK
            var uniqueMatches = matchSet.filter((value, index, self) => self.indexOf(value) === index);

            for (var i = 0, il = uniqueMatches.length; i < il; i++) {
                let currMatch = "{" + uniqueMatches[i] + "}";

                let reQuoteWrapped = new RegExp(`"${currMatch}"`, "g");
                ex.formula = ex.formula.replace(reQuoteWrapped, `expreng.currexpression.var("${currMatch}")`);
            }
        }

        // #endregion

        // #region Evaluation Functions

        // The actual evaluate call.  Will return the calculated value.
        ex.evaluate = function (viewID, gridRecord) {
            // We are passing in a grid record to the evaluate call explicitly.  However, as we support cross mvc expression calls, this will need to be revised.
            // We will then need to build ex.gridRecords from the caller so that we have all dependency items before evaluating...AL
            ex.gridRecords.clear();
            if (gridRecord != null) { ex.gridRecords.add(viewID, gridRecord); }

            // If we have a property to calculate, then we need to evaluate it's definitions for settings (as this is coming from mvc records)...AL
            if (ex.propertyToCalc != null) {
                if (gridRecord != null) {
                    let gv = gridRecord.gridValues.getVal(ex.propertyToCalc.PropertyID);
                    if (gv != null) {
                        let val = (gv.dynamicValueEdit == null) ? gv.dynamicValue : gv.dynamicValueEdit;
                        // If the value that we are calculating for is not set to only run once or it is but it's a new object, then execute the eval calculation...AL
                        if (!ex.propertyToCalc.Definition.IsCalculateExpressionOnlyOnceEnabled || val._State === types.EntityStates.NewObject) {

                            // determine if we have all the properties required for evaluating this expression.
                            let missingProperties = ex.propertyIDs.some((propLinker) => {
                                let testGV = gridRecord.gridValues.getVal(propLinker);
                                if (!testGV) {
                                    if (gridRecord.isDisposed) {
                                        return true;
                                    }
                                    let prop = app.dv.cache.properties.allProperties.getVal(propLinker),
                                        form = app.dv.cache.views.allViews.getVal(viewID),
                                        formName = (form && form.Definition && form.Definition.Name) || null;
                                    if (prop) {
                                        throw new Error(`Unable to evaluate expression; missing property: "${prop.Definition.Name}" ${formName ? `from form "${formName}"` : ''}`);
                                    }
                                    else {
                                        throw new Error(`Unable to evaluate expression; unknown property UID [${propLinker.UID}] ${formName ? `from form "${formName}"` : ''}`);
                                    }
                                }
                            });

                            if (missingProperties && gridRecord.isDisposed) {
                                // the values are missing because the record is disposed; simply return no value.
                                return "";
                            }

                            // Call the evaluate function off of the library so that we have scope to the library.
                            // Note we are also passing in a reference to this object so that we can call the 'vfp' function
                            // where the eval is processed so that we can have a scoped function to retrieve the values for this instance...AL
                            return exprEng.evaluate(ex);
                        }// Otherwise - just return the original value...AL
                        else {
                            // we have to take into consideration that the calling routine will be expecting any
                            // date/time value in the local time zone and it is in val.Value as GMT...WR
                            if (val.DataType === PropertyDataType.DateTime ||
                                val.DataType === PropertyDataType.StaticDateTime) {
                                if (val.Value != null) {
                                    return new Date(val.Value).addHours(0 - (val.Value.getTimezoneOffset() / 60));
                                }
                            } else {
                                return val.Value;
                            }
                        }
                    } else {
                        return "";
                    }
                } else {
                    return "";
                }
            }
                // Otherwise - just evaluate the formula...AL
            else {
                if (gridRecord != null) {
                    // determine if we have all the properties required for evaluating this expression.
                    let missingProperties = ex.propertyIDs.some((propLinker) => {
                        let testGV = gridRecord.gridValues.getVal(propLinker);
                        if (!testGV) {
                            if (gridRecord.isDisposed) {
                                return true;
                            }
                            let prop = app.dv.cache.properties.allProperties.getVal(propLinker),
                                form = app.dv.cache.views.allViews.getVal(viewID),
                                formName = (form && form.Definition && form.Definition.Name) || null;
                            if (prop) {
                                throw new Error(`Unable to evaluate expression; missing property: "${prop.Definition.Name}" ${formName ? `from form "${formName}"` : ''}`);
                            }
                            else {
                                throw new Error(`Unable to evaluate expression; unknown property UID [${propLinker.UID}] ${formName ? `from form "${formName}"` : ''}`);
                            }
                        }
                    });

                    if (missingProperties && gridRecord.isDisposed) {
                        // the values are missing because the record is disposed; simply return no value.
                        return "";
                    }

                }
                return exprEng.evaluate(ex);
            }
        };

        /**
         * This function is to only evaluate the expression; it will not attempt to return a value for the Property to Calculate...WR
         */
        ex.evaluateBool = function (viewID, gridRecord) {
            // We are passing in a grid record to the evaluate call explicitly.  However, as we support cross mvc expression calls, this will need to be revised.
            // We will then need to build ex.grdiRecords from the caller so that we have all dependency items before evaluating...AL
            ex.gridRecords.clear();
            if (gridRecord != null) { ex.gridRecords.add(viewID, gridRecord); }

            // evaluate the formula.
            return exprEng.evaluate(ex);
        };

        // This function is called during eval (on the library object) to get a value by view id and property id.
        // Note - the function name is vfp in case minifying would change the name (this would break the eval)...AL
        ex.vfp = function (viewID, propertyUID) {
            var gr = ex.gridRecords.getVal(viewID);

            if (gr != null) {
                // take the UID value from the parameter and wrap it in a property linker object.
                let gv = typeof propertyUID === "string"
                    ? gr.gridValues.getVal({ Type: "Property", UID: propertyUID })
                    : gr.gridValues.getVal(propertyUID);

                if (gv != null) {
                    // If there is a current editing value, then use it even it the value is null/empty...AL/AM
                    let dynamicValue = (gv.dynamicValueEdit != null) ? gv.dynamicValueEdit : gv.dynamicValue;
                    if (dynamicValue != null) {
                        switch (dynamicValue.DataType) {
                            case PropertyDataType.DateTime:
                            case PropertyDataType.StaticDateTime:
                                return gv.displayProperties.getDateValue();
                            case PropertyDataType.Binary:
                                return gv.displayProperties.getText();
                            default:
                                if (dynamicValue.Value != null) {
                                    return dynamicValue.Value;
                                }
                        }
                    }
                }
                else {
                    // the grid value was not found; we cannot return a value.
                    throw new Error(`Property [${typeof propertyUID === 'string' || Number.isNumeric(propertyUID) ? propertyUID : propertyUID.UID}] required for formula no longer available`);
                }
            }

            // Otherwise - we will need to spoof a dynamic value
            return '';
        };

        // This function is called during eval (on the library object) to get a grid value by view id and property id...BC
        ex.vfpgv = function (viewID, propertyUID) {
            var gr = ex.gridRecords.getVal(viewID),
                gv;

            if (gr != null) {
                if (typeof (propertyUID) == "string") {
                    gv = gr.gridValues.getVal({ "Type": "Property", "UID": propertyUID });
                }
                else {
                    gv = gr.gridValues.getVal(propertyUID);
                }
                if (gv != null) {
                    return gv;
                }
            }
            return null;
        };

        ex.var = function (varName) {
            let sessionVarVal = app.dv.cache.session.variables.getVal(varName);

            if (sessionVarVal != null) {
                switch (sessionVarVal.InfoValue.DataType) {
                    case PropertyDataType.DateTime:
                    case PropertyDataType.StaticDateTime:
                        if (sessionVarVal.InfoValue.Value == null) {
                            // if the value is null, we don't want to create a default date - we want to return null.
                            return sessionVarVal.InfoValue.Value;
                        }
                        // we need to return a new value object, not the source value object.
                        return new Date(sessionVarVal.InfoValue.Value);

                    default:
                        return sessionVarVal.InfoValue.Value;
                }
            }
            else {
                //NOTE: Once we are no longer in SL, we can deal with "real" values at this point and return null here...EK
                return '';
            }
        };

        // #endregion

        return ex;
    };

    // This Helper function will loop over the expressions passed in to evaluate for statuses, and set the statuses accordingly...AL
    expression.evaluateStatus = function (viewID, gridRecord, orderedExpressions, codePropertyID) {
        var se, currGV;
        for (var i = 0, il = orderedExpressions.length; i < il; i++) {
            se = orderedExpressions[i];
            if (se.evaluateBool(viewID, gridRecord, true) == true) {
                currGV = gridRecord.gridValues.getVal(se.propertyToCalc.PropertyID);
                app.dv.entities.gridValue.setStatus(currGV, se.status, true);
                break;
            }
        }
    };

    expression.dispose = function (exp) {
        exp.propertyToCalc = null;
        if (Array.isArray(exp.propertyIDs)) {
            exp.propertyIDs.length = 0;
        }
        if (exp.gridRecords != null && exp.gridRecords.clear != null) {
            exp.gridRecords.clear();
        }
        exp.gridRecords = null;
        exp.mvc = null;
    };

    return expression;
});
